import React, { useCallback, useLayoutEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { APICore } from "./api/apiCore";
import { client } from "./amplifyClient";
import { updateUserSettings } from "./graphql/userSettings.mutations";
import { updateUser } from "../redux/actions";
import { getHideDataConfig, getSnapshot, setPreferences } from "./hideData";
import HideDataContext from "./hideDataContext";

const api = new APICore();

// Bridge between the Redux user and the Privacy Mode store.
//
// The state does NOT live here any more. It lives in the store in helpers/hideData.js, which
// useHideData subscribes to and the hook-free masking points read directly. This provider is
// left with two jobs:
//
//   1. Mirror the PREFERENCES from `user.settings.hideData` into the store — on login, and when
//      refreshUserPermissions() in App.js pulls in a config saved on another device.
//   2. Provide `saveConfig`, which needs Redux, the GraphQL client and the current user and can
//      therefore not live in the store.
//
// The SWITCH bypasses this provider: setEnabled() writes the device-local key and notifies the
// subscribers, nothing else. No dispatch, no database write — toggling happens constantly and
// must be instant, and 60 effects in this app depend on `[user]`, 26 of which refetch.
export const HideDataProvider = ({ children }) => {
  const { user } = useSelector((state) => ({ user: state.Auth.user }));
  const dispatch = useDispatch();

  // Keyed on the serialised preferences rather than the user object, so an unrelated user
  // update does not restart the hydration.
  const storedPreferences = JSON.stringify(getHideDataConfig(user));

  // useLayoutEffect, not useEffect: layout effects run AFTER the children render but BEFORE the
  // paint. With useEffect there would be a visible frame in which a class still sits on its
  // default — for `documents` (default off) that means a file name in the clear.
  //
  // The previous version wrote a module variable during render instead. That avoided the flash
  // too, but was a side effect in a function React may call more than once and may throw away.
  useLayoutEffect(() => {
    // setPreferences carries `enabled` over from the current state — the switch belongs to the
    // device. Taking it from here would switch the mode off on every settings-page save.
    setPreferences(JSON.parse(storedPreferences));
  }, [storedPreferences]);

  // The preferences. An ordinary settings write with a dispatch: it happens once, on the
  // settings page, and the user leaves that page afterwards.
  const saveConfig = useCallback(
    (preferences) => {
      setPreferences(preferences);

      if (!user?.id) return Promise.resolve();

      // `enabled` is deliberately not written to settings — it belongs to the device.
      // eslint-disable-next-line no-unused-vars
      const { enabled, ...stored } = getSnapshot();
      // A COPY of the current settings as the baseline. `settings` is one blob that ~20 writers
      // rewrite wholesale; assigning into it would mutate the Redux store outside a dispatch.
      const currentSettings =
        typeof user.settings === "string"
          ? JSON.parse(user.settings)
          : { ...(user.settings || {}) };
      const newSettings = { ...currentSettings, hideData: stored };

      api.setLoggedInUserInLocalStorage({ ...user, settings: newSettings });
      dispatch(updateUser({ ...user, settings: newSettings }));

      // The rejection is deliberately NOT swallowed: the settings page awaits this promise and
      // shows a toast. Catching here made every failed save report success and cleared the
      // unsaved-changes guard, so the user walked away believing it had been stored.
      return client.graphql({
        query: updateUserSettings,
        variables: {
          input: { id: user.id, settings: JSON.stringify(newSettings) },
        },
      });
    },
    [user, dispatch],
  );

  const value = useMemo(() => ({ saveConfig }), [saveConfig]);

  return (
    <HideDataContext.Provider value={value}>
      {children}
    </HideDataContext.Provider>
  );
};

// Re-exported so older imports that point at the provider keep working. New code should import
// the context from ./hideDataContext, which does not drag the GraphQL client along.
export default HideDataContext;
